import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";

// @mui material components
import {
  Card,
  CircularProgress,
  Fade,
  Grid,
  Icon,
  InputLabel,
  Modal,
  Tooltip,
} from "@mui/material";

import { gql, useLazyQuery, useMutation } from "@apollo/client";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { validateDNI, validateEmail } from "utils/validations";
import { validatePhone } from "utils/validations";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "80%", xxl: "70%" },
  overflowY: "auto",
  p: 3,
};

export default function ModalResponsableTecnico({
  open,
  handleClose,
  refetch,
  idResponsableTecnico,
}) {
  const [responsableTecnico, setResponsableTecnico] = useState({});
  const { handleSnackbar } = useContext(MessageManager);

  const handleChanges = (e) => {
    setResponsableTecnico({
      ...responsableTecnico,
      [e.target.name]: e.target.value,
    });
  };

  const [getResponsableTecnico, { data: dataResponsableTecnico }] = useLazyQuery(
    gql`
      query getResponsableTecnico($id: ID!) {
        responsableTecnico(id: $id) {
          __typename @skip(if: true)
          id
          nombre
          apellido
          dni
          matricula
          telefono
          email
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  const [saveResponsableTecnico, { loading: loadingSave }] = useMutation(
    gql`
      mutation saveResponsableTecnico($input: ResponsableTecnicoInput!) {
        saveResponsableTecnico(input: $input) {
          id
        }
      }
    `
  );

  const [createResponsableTecnico, { loading: loadingCreate }] = useMutation(
    gql`
      mutation createResponsableTecnico($input: ResponsableTecnicoInput!) {
        createResponsableTecnico(input: $input) {
          id
        }
      }
    `
  );

  const handleSave = () => {
    if (idResponsableTecnico) {
      saveResponsableTecnico({
        variables: {
          input: {
            id: idResponsableTecnico,
            ...responsableTecnico,
          },
        },
      })
        .then(() => {
          refetch();
          handleSnackbar("Responsable Técnico guardado correctamente.", "success");
          handleClose();
        })
        .catch((e) => {
          if (e.message) {
            handleSnackbar(e.message, "error");
          } else {
            handleSnackbar("Error al guardar el Responsable Técnico", "error");
          }
        });
    } else {
      createResponsableTecnico({
        variables: {
          input: { ...responsableTecnico },
        },
      })
        .then((res) => {
          refetch();
          handleSnackbar("Responsable Técnico creado correctamente", "success");
          handleClose();
        })
        .catch((e) => {
          if (e.message) {
            handleSnackbar(e.message, "error");
          } else {
            handleSnackbar("Error al crear el Responsable Técnico", "error");
          }
        });
    }
  };

  useEffect(() => {
    if (idResponsableTecnico && open) {
      getResponsableTecnico({ variables: { id: idResponsableTecnico } });
    }
  }, [idResponsableTecnico, open]);

  useEffect(() => {
    if (dataResponsableTecnico && idResponsableTecnico && open) {
      setResponsableTecnico(dataResponsableTecnico.responsableTecnico);
    } else {
      setResponsableTecnico({});
    }
  }, [dataResponsableTecnico, idResponsableTecnico, open]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center">
              <SoftTypography variant="h6">
                {idResponsableTecnico
                  ? "Editar Responsable Técnico"
                  : "Agregar Responsable Técnico"}
              </SoftTypography>
              <Tooltip title="Cerrar" placement="top">
                <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                  close
                </Icon>
              </Tooltip>
            </SoftBox>
            <SoftBox py={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor="nombre">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Nombre
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="nombre"
                    placeholder="Ingrese el nombre"
                    type="text"
                    name="nombre"
                    value={responsableTecnico?.nombre || ""}
                    onChange={handleChanges}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor="apellido">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Apellido
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="apellido"
                    placeholder="Ingrese el apellido"
                    type="text"
                    name="apellido"
                    value={responsableTecnico?.apellido || ""}
                    onChange={handleChanges}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor="dni">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      DNI
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="dni"
                    placeholder="Ingrese el DNI"
                    name="dni"
                    value={responsableTecnico?.dni || ""}
                    error={
                      responsableTecnico?.dni?.length > 0 && !validateDNI(responsableTecnico?.dni)
                    }
                    onChange={handleChanges}
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor="matricula">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Matrícula
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="matricula"
                    placeholder="Ingrese la matrícula"
                    name="matricula"
                    value={responsableTecnico?.matricula || ""}
                    onChange={handleChanges}
                    inputProps={{ min: 0, step: 1 }}
                    type="number"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor="telefono">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Teléfono
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="telefono"
                    placeholder="Ingrese el teléfono"
                    type="text"
                    name="telefono"
                    value={responsableTecnico?.telefono || ""}
                    error={
                      responsableTecnico?.telefono?.length > 0 &&
                      !validatePhone(responsableTecnico?.telefono)
                    }
                    onChange={handleChanges}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor="email">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Email
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="email"
                    placeholder="Ingrese el email"
                    type="text"
                    name="email"
                    value={responsableTecnico?.email || ""}
                    error={
                      responsableTecnico?.email?.length > 0 &&
                      !validateEmail(responsableTecnico?.email)
                    }
                    onChange={handleChanges}
                  />
                </Grid>
              </Grid>
            </SoftBox>
            <SoftBox display="flex" justifyContent="flex-end" alignItems="center">
              <SoftButton
                color="primary"
                onClick={handleClose}
                sx={{
                  marginRight: 1,
                }}
              >
                <Icon sx={{ fontWeight: "light" }}>cancel</Icon>
                &nbsp;Cancelar
              </SoftButton>
              <SoftButton
                color="gobSecondary"
                disabled={
                  loadingSave ||
                  loadingCreate ||
                  !responsableTecnico?.nombre ||
                  !responsableTecnico?.apellido ||
                  !responsableTecnico?.dni ||
                  !responsableTecnico?.telefono ||
                  !responsableTecnico?.email ||
                  (responsableTecnico?.dni?.length > 0 && !validateDNI(responsableTecnico?.dni)) ||
                  (responsableTecnico?.telefono?.length > 0 &&
                    !validatePhone(responsableTecnico?.telefono)) ||
                  (responsableTecnico?.email?.length > 0 &&
                    !validateEmail(responsableTecnico?.email))
                }
                onClick={handleSave}
              >
                {loadingSave || loadingCreate ? (
                  <CircularProgress size={15} sx={{ marginRight: 0.5 }} color="inherit" />
                ) : (
                  <Icon sx={{ fontWeight: "light" }}>save</Icon>
                )}
                &nbsp;Guardar
              </SoftButton>
            </SoftBox>
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalResponsableTecnico.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  refetch: PropTypes.func,
  idResponsableTecnico: PropTypes.string,
};
